import {
  Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Input, Spinner,
} from '@heroui/react';
import { RotateCcw, Search } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';

import { useHeroToast } from '../../../hooks';
import { useGetUnsubscribes, useResubscribeEmails } from '../../../queries';

interface UnsubscribesDrawerProps {
  onClose: () => void;
}

const formatDate = (value: number | null): string => (value
  ? new Date(value).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '');

const sourceLabel = (source: string): string => (source === 'admin' ? 'ręcznie' : 'link w mailu');

/** Lista adresów, do których wysyłka nie pójdzie, z możliwością cofnięcia wypisania. */
export const UnsubscribesDrawer: FC<UnsubscribesDrawerProps> = ({ onClose }) => {
  const { showError, showSuccess } = useHeroToast();
  const { data: entries = [], isLoading, isError, error } = useGetUnsubscribes();
  const { mutate: resubscribe, isPending } = useResubscribeEmails();
  const [term, setTerm] = useState('');

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((entry) => entry.email.toLowerCase().includes(needle));
  }, [entries, term]);

  const handleResubscribe = (email: string) => {
    resubscribe({ emails: [email] }, {
      onSuccess: () => showSuccess(`${email} znów będzie dostawać wiadomości.`),
      onError: () => showError('Nie udało się cofnąć wypisania.'),
    });
  };

  return (
    <Drawer
      isOpen
      placement="right"
      radius="none"
      classNames={{ base: 'w-full sm:w-1/2 sm:max-w-none' }}
      onClose={onClose}
    >
      <DrawerContent>
        <DrawerHeader className="flex-col items-start gap-1">
          <span className="text-base font-semibold text-gray-900">Wypisani z wysyłek</span>
          <span className="text-xs font-normal text-gray-500">
            {`Do tych adresów nie wyślemy maila. Wypisanych: ${entries.length}.`}
          </span>
        </DrawerHeader>

        <DrawerBody className="gap-4">
          <Input
            size="sm"
            placeholder="Szukaj adresu"
            startContent={<Search className="h-4 w-4 text-gray-400" />}
            value={term}
            onValueChange={setTerm}
          />

          {isLoading && <Spinner size="sm" className="self-center" />}

          {isError && (
            <p className="rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger">
              {(error as { code?: string })?.code === 'permission-denied'
                ? 'Brak dostępu do listy. Wdróż reguły Firestore (firebase deploy --only firestore:rules).'
                : 'Nie udało się pobrać listy wypisanych.'}
            </p>
          )}

          {!isLoading && !isError && visible.length === 0 && (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
              {term ? 'Brak adresu pasującego do wyszukiwania.' : 'Nikt się jeszcze nie wypisał.'}
            </p>
          )}

          {visible.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Adres</th>
                    <th className="px-3 py-2">Skąd</th>
                    <th className="px-3 py-2">Kiedy</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((entry) => (
                    <tr key={entry.email} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{entry.email}</td>
                      <td className="px-3 py-2 text-gray-500">{sourceLabel(entry.source)}</td>
                      <td className="px-3 py-2 text-gray-500">{formatDate(entry.createdAt)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="light"
                          isDisabled={isPending}
                          startContent={<RotateCcw className="h-4 w-4" />}
                          onPress={() => handleResubscribe(entry.email)}
                        >
                          Przywróć
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Button variant="light" onPress={onClose}>Zamknij</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
